import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Creator } from "@/entities/Creator";
import { Star, MapPin, Play, ArrowRight, CalendarDays, Search, Award } from "lucide-react";
const CATEGORIES = ["All", "Artist (Entertainment)", "Content Creator", "Craftsman (Build To Spec)", "Healer (Remedies; Protocol)", "Manifestor", "Motivator", "Soul Worker (Passion Service)", "Problem Solver", "Visionary"];
const SCHEDULE_CALENDAR_URL = "https://calendly.com/creatorswarehouse/creator-interview";

function ScorePill({ score }) {
  const tier = score >= 80 ? { label: "Elite", color: "bg-amber-500 text-white" }
    : score >= 60 ? { label: "Pro", color: "bg-purple-500 text-white" }
    : score >= 40 ? { label: "Rising", color: "bg-blue-500 text-white" }
    : { label: "New", color: "bg-gray-400 text-white" };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tier.color}`}>
      {tier.label} · {score}
    </span>
  );
}

// Simple score estimate from creator fields alone (no async needed on list page)
function quickScore(creator) {
  const salesScore = Math.min((creator.total_sales || 0) / 2, 30);
  const videoScore = creator.youtube_video_url ? 5 : 0;
  const bioScore = creator.bio?.length > 100 ? 5 : 2;
  const profileScore = creator.avatar ? 5 : 0;
  const bannerScore = creator.banner_image ? 5 : 0;
  return Math.round(Math.min(salesScore + videoScore + bioScore + profileScore + bannerScore, 100));
}

export default function Creators() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");

  useEffect(() => {
    Creator.list("-total_sales", 100).then(data => {
      setCreators(data);
      setLoading(false);
      const params = new URLSearchParams(window.location.search);
      const cat = params.get("category");
      if (cat) setActiveCategory(cat);
    });
  }, []);

  const filtered = creators.filter(c => {
    const matchCat = activeCategory === "All" || c.primary_category === activeCategory || c.specialty === activeCategory;
    const matchSearch = !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.specialty?.toLowerCase().includes(search.toLowerCase()) || c.primary_category?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      {/* Header */}
      <div className="bg-[#1a1a1a] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest mb-3">The People Behind the Products</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">Meet Our Creators</h1>
          <p className="text-gray-400 text-sm max-w-xl mx-auto leading-relaxed mb-8">
            Every creator has a story, a skill, and something to offer. Explore profiles, watch intros, and connect with the talent that powers Creators Warehouse.
          </p>
          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search creators..."
              className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#c8a96e]"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-8 scrollbar-hide">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`flex-shrink-0 text-xs font-semibold px-4 py-2 rounded-full border transition-all ${
                activeCategory === cat
                  ? "bg-[#1a1a1a] text-white border-[#1a1a1a]"
                  : "bg-white text-gray-600 border-[#e8e4de] hover:border-[#c8a96e] hover:text-[#c8a96e]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#e8e4de] overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-100" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-10 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <p className="text-sm">No creators found matching your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map(creator => {
              const score = quickScore(creator);
              return (
                <Link
                  key={creator.id}
                  to={createPageUrl(`CreatorProfile?id=${creator.id}`)}
                  className="group bg-white rounded-2xl border border-[#e8e4de] overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Banner */}
                  <div className="relative h-44 overflow-hidden bg-[#1a1a1a]">
                    <img
                      src={creator.banner_image || creator.avatar || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=300&fit=crop"}
                      alt={creator.name}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex gap-1.5">
                      {creator.is_featured && (
                        <span className="bg-[#c8a96e] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Featured</span>
                      )}
                      {creator.youtube_video_url && (
                        <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Play className="w-2.5 h-2.5 fill-white" /> Video
                        </span>
                      )}
                    </div>
                    {/* Score */}
                    <div className="absolute top-3 right-3">
                      <ScorePill score={score} />
                    </div>
                    {/* Avatar */}
                    <div className="absolute bottom-3 left-4">
                      <img src={creator.avatar} alt={creator.name} className="w-10 h-10 rounded-full border-2 border-white object-cover shadow" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-1">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-[#1a1a1a] group-hover:text-[#c8a96e] transition-colors truncate">{creator.name}</h3>
                        <p className="text-xs text-[#c8a96e] font-medium truncate">{creator.primary_category || creator.specialty}</p>
                      </div>
                      {creator.rating && (
                        <div className="flex items-center gap-0.5 flex-shrink-0 ml-2">
                          <Star className="w-3 h-3 fill-[#c8a96e] text-[#c8a96e]" />
                          <span className="text-xs font-semibold">{creator.rating}</span>
                        </div>
                      )}
                    </div>
                    {creator.location && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 mb-2"><MapPin className="w-3 h-3" />{creator.location}</p>
                    )}
                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-3">{creator.bio}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-[#e8e4de]">
                      <span className="text-xs text-gray-400">{creator.total_sales || 0} sales</span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#c8a96e] group-hover:gap-2 transition-all">
                        {creator.youtube_video_url ? <><Play className="w-3 h-3" /> Watch & Explore</> : <>View Profile <ArrowRight className="w-3 h-3" /></>}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 bg-[#1a1a1a] rounded-2xl p-10 text-center">
          <Award className="w-10 h-10 text-[#c8a96e] mx-auto mb-4" />
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-3">Want to Join Our Community?</h2>
          <p className="text-gray-400 text-sm mb-8 max-w-md mx-auto">
            Apply to become a creator. Our staff will produce a professional video intro to showcase your story, projects, and offerings.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={createPageUrl("CreatorDashboard")}
              className="inline-flex items-center gap-2 bg-[#c8a96e] hover:bg-[#a8893e] text-white font-semibold px-8 py-3 rounded-full transition-colors text-sm"
            >
              Apply to Become a Creator <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={SCHEDULE_CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/30 hover:border-[#c8a96e] text-white hover:text-[#c8a96e] font-semibold px-8 py-3 rounded-full transition-colors text-sm"
            >
              <CalendarDays className="w-4 h-4" /> Schedule Your Video Interview
            </a>
          </div>
          <p className="text-gray-600 text-xs mt-4">Our staff will film and produce your intro video — no experience needed.</p>
        </div>
      </div>
    </div>
  );
}